import { useState, useEffect } from "react";
import { loanAPI } from "../utils/api";

export default function LoanPage({ onAskAI }) {
  const [costLakhs, setCostLakhs] = useState(25);
  const [selfFundingPct, setSelfFundingPct] = useState(20);
  const [tenureYears, setTenureYears] = useState(15);
  const [interestRate, setInterestRate] = useState(8.75);

  const [loanData, setLoanData] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateSchedule = async () => {
    setLoading(true);
    try {
      const totalCostINR = costLakhs * 100000;
      const res = await loanAPI.calculateLoanSchedule({
        total_project_cost: totalCostINR,
        self_funding_pct: parseFloat(selfFundingPct),
        loan_tenure_years: parseInt(tenureYears),
        interest_rate_pct: parseFloat(interestRate),
      });
      setLoanData(res.data);
    } catch (err) {
      console.error("Loan calculation error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    calculateSchedule();
  }, [costLakhs, selfFundingPct, tenureYears, interestRate]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="tool-page-container">
      <div className="page-header">
        <div className="header-badge">🏦 BANK FINANCE &amp; STAGE DISBURSEMENT</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <h1>Construction Home Loan &amp; Tranche Planner 📊</h1>
            <p>Calculate monthly EMI, Pre-EMI, and bank 7-stage disbursement schedule based on your budget.</p>
          </div>
          <button className="action-pill-btn" onClick={handlePrint}>
            🖨️ Print Loan Schedule
          </button>
        </div>
      </div>

      <div className="loan-calculator-grid">
        {/* Left Card: Input Sliders */}
        <div className="card loan-inputs-card">
          <h2 className="card-title">⚙️ Loan Parameters</h2>

          {/* Slider 1: Total Cost */}
          <div className="slider-group">
            <div className="slider-label-row">
              <label>Total Construction Budget:</label>
              <span className="slider-value-badge">₹{costLakhs} Lakhs</span>
            </div>
            <input
              type="range"
              min="5"
              max="150"
              step="1"
              value={costLakhs}
              onChange={(e) => setCostLakhs(Number(e.target.value))}
              className="custom-range-slider"
            />
            <div className="slider-minmax">
              <span>₹5L</span>
              <span>₹50L</span>
              <span>₹1.5 Cr</span>
            </div>
          </div>

          {/* Slider 2: Own Contribution */}
          <div className="slider-group">
            <div className="slider-label-row">
              <label>Self / Own Contribution:</label>
              <span className="slider-value-badge">{selfFundingPct}% (₹{((costLakhs * selfFundingPct) / 100).toFixed(2)}L)</span>
            </div>
            <input
              type="range"
              min="10"
              max="60"
              step="5"
              value={selfFundingPct}
              onChange={(e) => setSelfFundingPct(Number(e.target.value))}
              className="custom-range-slider"
            />
            <div className="slider-minmax">
              <span>10% (Min)</span>
              <span>20% (Standard)</span>
              <span>60%</span>
            </div>
          </div>

          {/* Slider 3: Loan Tenure */}
          <div className="slider-group">
            <div className="slider-label-row">
              <label>Loan Tenure (Years):</label>
              <span className="slider-value-badge">{tenureYears} Years ({tenureYears * 12} Months)</span>
            </div>
            <input
              type="range"
              min="5"
              max="30"
              step="1"
              value={tenureYears}
              onChange={(e) => setTenureYears(Number(e.target.value))}
              className="custom-range-slider"
            />
            <div className="slider-minmax">
              <span>5 Yrs</span>
              <span>15 Yrs</span>
              <span>30 Yrs</span>
            </div>
          </div>

          {/* Slider 4: Interest Rate */}
          <div className="slider-group">
            <div className="slider-label-row">
              <label>Annual Interest Rate (p.a.):</label>
              <span className="slider-value-badge">{interestRate}%</span>
            </div>
            <input
              type="range"
              min="7.5"
              max="13.0"
              step="0.25"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="custom-range-slider"
            />
            <div className="slider-minmax">
              <span>7.5% (Repo linked)</span>
              <span>8.75% (SBI/HDFC Avg)</span>
              <span>13.0%</span>
            </div>
          </div>

          <div className="loan-tip-box">
            💡 <strong>Construction Loan Rule:</strong> Banks do not disburse the full loan amount at once; funds are released in stage-wise tranches as site work progresses (Foundation, Slab, Plinth). During the construction period, you only pay <strong>Pre-EMI (Simple Interest)</strong> on the disbursed amount.
          </div>
        </div>

        {/* Right Column: Financial KPIs & Tranche Schedule */}
        <div className="loan-results-column">
          {loanData && (
            <>
              {/* Summary KPIs */}
              <div className="loan-kpi-grid">
                <div className="kpi-card emi-card">
                  <div className="kpi-label">Monthly Regular EMI (After Completion)</div>
                  <div className="kpi-value">₹{loanData.monthly_emi.toLocaleString("en-IN")}/mo</div>
                  <div className="kpi-sub">Total {loanData.tenure_years} Years tenure</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-label">Bank Loan Sanctioned</div>
                  <div className="kpi-value">₹{(loanData.loan_amount / 100000).toFixed(2)} Lakhs</div>
                  <div className="kpi-sub">{100 - selfFundingPct}% of Total Cost</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-label">Your Self Contribution</div>
                  <div className="kpi-value">₹{(loanData.self_contribution / 100000).toFixed(2)} Lakhs</div>
                  <div className="kpi-sub">{selfFundingPct}% upfront equity</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-label">Total Interest Payable</div>
                  <div className="kpi-value">₹{(loanData.total_interest_payable / 100000).toFixed(2)} Lakhs</div>
                  <div className="kpi-sub">Total repayment: ₹{(loanData.total_repayment / 100000).toFixed(2)}L</div>
                </div>
              </div>

              {/* Stage-wise Tranche Disbursement Table */}
              <div className="card tranche-card">
                <div className="tranche-header">
                  <h3 className="card-title">📋 7-Stage Bank Disbursement &amp; Pre-EMI Schedule</h3>
                  <span className="badge-pill">SBI / HDFC / ICICI Standard Norms</span>
                </div>

                <div className="table-responsive">
                  <table className="custom-data-table">
                    <thead>
                      <tr>
                        <th>Stage / Construction Milestone</th>
                        <th>Tranche %</th>
                        <th>Amount Released (₹)</th>
                        <th>Cumulative Drawdown</th>
                        <th>Est. Pre-EMI / mo</th>
                        <th>Required Bank Doc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanData.disbursement_schedule.map((tranche, idx) => (
                        <tr key={idx}>
                          <td>
                            <strong>{tranche.stage}</strong>
                          </td>
                          <td>
                            <span className="pct-badge">{tranche.tranche_pct}%</span>
                          </td>
                          <td className="amount-cell">₹{(tranche.amount_inr / 100000).toFixed(2)} Lakhs</td>
                          <td>₹{(tranche.cumulative_disbursed / 100000).toFixed(2)} Lakhs</td>
                          <td className="interest-cell">₹{tranche.pre_emi_monthly.toLocaleString("en-IN")}/mo</td>
                          <td className="doc-cell">{tranche.doc_needed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="tranche-footer-note">
                  ℹ️ <strong>Pre-EMI Advice:</strong> Principal repayment does not start during construction, keeping monthly payments lower. Regular full EMI (₹{loanData.monthly_emi.toLocaleString("en-IN")}) begins once construction is complete and the Occupancy Certificate is issued.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
